import { useEffect } from 'react'

/**
 * ServicesCarousel - turns the homepage services grid into a single
 * continuously-scrolling row (marquee), so it takes one row instead of two.
 *
 * Markup ships the `.service-card` items in `[data-svc-marquee] .svc-marquee-track`.
 * This overlay clones the set to fill the loop and drives a requestAnimationFrame
 * marquee at a constant px/second speed. The loop period is measured from the
 * first clone's offset (so the flex `gap` is accounted for and the wrap is
 * seamless). Pauses on hover / focus, supports drag / swipe (pointer events)
 * with a click-guard so a drag never triggers a card link, recomputes on
 * resize, and respects prefers-reduced-motion.
 *
 * Same DOM-overlay pattern as ClientCarousel / TestimonialCarousel.
 */
const SPEED = 36 // px per second

function initMarquee(root) {
  if (root.dataset.svcReady === '1') return
  const viewport = root.querySelector('.svc-marquee-viewport')
  const track = root.querySelector('.svc-marquee-track')
  const originals = track ? Array.from(track.children) : []
  if (!viewport || !track || originals.length === 0) return
  root.dataset.svcReady = '1'

  let baseWidth = 0

  function fill() {
    track.querySelectorAll('[data-svc-clone]').forEach((n) => n.remove())
    const setWidth = originals.reduce((w, el) => w + el.getBoundingClientRect().width, 0)
    if (setWidth <= 0) return
    const need = viewport.getBoundingClientRect().width * 2 + setWidth
    let total = setWidth
    do {
      originals.forEach((el) => {
        const c = el.cloneNode(true)
        c.setAttribute('data-svc-clone', '')
        c.setAttribute('aria-hidden', 'true')
        // aria-hidden only hides content from screen readers - it does not
        // remove native tab-stops, so the cloned card's link would otherwise
        // stay keyboard-focusable while invisible to assistive tech.
        c.querySelectorAll('a, button').forEach((el2) => el2.setAttribute('tabindex', '-1'))
        track.appendChild(c)
      })
      total += setWidth
    } while (total < need)
    // Period = distance from the first original to its first clone (includes gaps).
    const firstClone = track.children[originals.length]
    baseWidth = firstClone ? firstClone.offsetLeft - track.children[0].offsetLeft : setWidth
  }

  let pos = 0
  let paused = false
  let dragging = false
  /* Signed px still to travel from an arrow press. Non-zero means a step is in
     flight; the frame loop below drains it. Presses ADD to it, so tapping the
     arrow repeatedly queues cards instead of restarting the same step. */
  let remain = 0
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  function wrap() {
    if (baseWidth <= 0) return
    pos = ((pos % baseWidth) + baseWidth) % baseWidth - baseWidth
    if (pos <= -baseWidth) pos += baseWidth
  }
  function apply() { track.style.transform = `translate3d(${pos}px,0,0)` }

  fill()
  apply()

  let last = null
  function frame(t) {
    const dt = last == null ? 0 : (t - last) / 1000
    last = t
    const live = root.offsetParent !== null && dt > 0
    /* An arrow step runs even while `paused`, because the cursor is necessarily
       over the marquee (that is what paused means) when someone clicks an arrow.
       It yields to `dragging` so a step never fights a finger. */
    if (remain !== 0 && !dragging && live) {
      /* Rate proportional to what is left = ease-out, with a floor so the tail
         does not crawl. No timing bookkeeping, and queued presses just extend
         the distance rather than restarting an animation. */
      const rate = Math.max(320, Math.abs(remain) * 5)
      let d = Math.sign(remain) * rate * Math.min(dt, 0.05)
      if (Math.abs(d) >= Math.abs(remain)) d = remain
      pos += d
      remain -= d
      wrap()
      apply()
    } else if (!paused && !dragging && !reduce && live) {
      pos -= SPEED * Math.min(dt, 0.05)
      wrap()
      apply()
    }
    requestAnimationFrame(frame)
  }
  requestAnimationFrame(frame)

  viewport.addEventListener('mouseenter', () => { paused = true })
  viewport.addEventListener('mouseleave', () => { paused = false })
  root.addEventListener('focusin', () => { paused = true })
  root.addEventListener('focusout', () => { paused = false })

  let startX = 0
  let startPos = 0
  let activeId = null
  let moved = 0
  let captured = false

  /* Slop, in px, below which a gesture is a CLICK and not a drag. Shared by the
     capture decision below and the click-guard further down so the two cannot
     disagree about what counts as a drag. */
  const DRAG_SLOP = 6

  viewport.addEventListener('pointerdown', (e) => {
    dragging = true
    activeId = e.pointerId
    startX = e.clientX
    startPos = pos
    moved = 0
    captured = false
    viewport.classList.add('is-dragging')
    /* DO NOT setPointerCapture here. This used to capture on every pointerdown
       and it silently broke every card link in the marquee - the reported
       "clicking a service card doesn't go to the page".
       Per the Pointer Events spec, while an element holds pointer capture the
       pointer events AND the compatibility mouse events derived from them -
       including `click` - are retargeted to the capturing element. So pressing
       on a card's link produced:
           pointerdown -> A.btn                    (correct)
           pointerup   -> DIV.svc-marquee-viewport (retargeted)
           click       -> DIV.svc-marquee-viewport (retargeted)
       An anchor only performs its default navigation when the click's target is
       inside it, so the click landed on the viewport div and nothing happened.
       Nothing was preventDefault()ed and no error was thrown, which is why this
       looked like a routing or cache problem rather than a DOM one.
       Capture is still wanted for a REAL drag (it keeps pointermove coming when
       the cursor leaves the viewport), so it is acquired in pointermove below,
       once the gesture has proven itself a drag. */
  })
  viewport.addEventListener('pointermove', (e) => {
    if (!dragging || e.pointerId !== activeId) return
    const dx = e.clientX - startX
    moved = Math.max(moved, Math.abs(dx))
    /* Promote to a captured drag only after the slop is exceeded. A click never
       gets here, so a click is never retargeted. */
    if (!captured && moved > DRAG_SLOP) {
      try { viewport.setPointerCapture(e.pointerId); captured = true } catch { /* older browsers */ }
    }
    pos = startPos + dx
    wrap()
    apply()
  })
  function endDrag() {
    if (!dragging) return
    // Release before clearing activeId - releasePointerCapture needs the id.
    if (captured) {
      try { viewport.releasePointerCapture(activeId) } catch { /* already released */ }
      captured = false
    }
    dragging = false
    activeId = null
    viewport.classList.remove('is-dragging')
    last = null
  }
  viewport.addEventListener('pointerup', endDrag)
  viewport.addEventListener('pointercancel', endDrag)
  viewport.addEventListener('dragstart', (e) => e.preventDefault())
  // A real drag must not fire the card's link click.
  viewport.addEventListener('click', (e) => {
    if (moved > DRAG_SLOP) { e.preventDefault(); e.stopPropagation() }
  }, true)

  /* ---- prev / next arrows ----
     Added so a card can be brought over without waiting for the marquee to
     carry it round, which at 36 px/s is up to ~80s for a full cycle.

     Built here rather than shipped in Pentabay.html for two reasons: fill()
     clones everything inside .svc-marquee-track, so a button in the track would
     be duplicated once per copy; and anything inside .svc-marquee-viewport sits
     on the drag surface, where pointerdown starts a drag. Appending to `root`
     (.svc-marquee, already position:relative) avoids both. */
  function cardStep() {
    // One card plus one gap, measured rather than assumed - the card width is a
    // clamp() and the gap is a flex `gap`, so neither is a constant here.
    const a = track.children[0]
    const b = track.children[1]
    if (!a || !b) return viewport.getBoundingClientRect().width
    const step = b.offsetLeft - a.offsetLeft
    return step > 0 ? step : viewport.getBoundingClientRect().width
  }

  function step(dir) {
    const d = dir * cardStep()
    if (reduce) {
      // No glide for prefers-reduced-motion: land on the card immediately.
      pos -= d
      remain = 0
      wrap()
      apply()
      return
    }
    remain -= d
  }

  const ARROW = {
    prev: 'M15 18l-6-6 6-6',
    next: 'M9 18l6-6-6-6',
  }
  function makeArrow(dir) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = `svc-arrow svc-${dir === -1 ? 'prev' : 'next'}`
    btn.setAttribute('aria-label', dir === -1 ? 'Previous services' : 'Next services')
    btn.innerHTML =
      `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">` +
      `<path d="${dir === -1 ? ARROW.prev : ARROW.next}" stroke="currentColor" stroke-width="2" ` +
      `stroke-linecap="round" stroke-linejoin="round"/></svg>`
    btn.addEventListener('click', (e) => { e.preventDefault(); step(dir) })
    /* The marquee pauses on pointer-enter of the viewport, but the arrows sit
       outside it, so hovering an arrow would let the row keep sliding under the
       cursor while someone is trying to aim. Pause on the arrows too. */
    btn.addEventListener('mouseenter', () => { paused = true })
    btn.addEventListener('mouseleave', () => { paused = false })
    root.appendChild(btn)
    return btn
  }
  makeArrow(-1)
  makeArrow(1)

  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => { fill(); wrap(); apply() })
    ro.observe(viewport)
  } else {
    window.addEventListener('resize', () => { fill(); wrap(); apply() })
  }
}

export default function ServicesCarousel() {
  useEffect(() => {
    function initAll() {
      document
        .querySelectorAll('[data-svc-marquee]:not([data-svc-ready="1"])')
        .forEach(initMarquee)
    }
    initAll()
    const mo = new MutationObserver(initAll)
    mo.observe(document.body, { childList: true, subtree: true })
    return () => mo.disconnect()
  }, [])
  return null
}
